export type Me = {
  id: string;
  authUserId: string;
  email: string;
  fullName: string;
  role: 'admin' | 'asistente';
};

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export function getMe(): Promise<Me> {
  return apiFetch<Me>('/api/me');
}

export type AdminUser = Me & { createdAt: string };

export function listUsers(): Promise<AdminUser[]> {
  return apiFetch<AdminUser[]>('/api/admin/users');
}

export type CreateUserInput = {
  email: string;
  password?: string;
  fullName: string;
  role: 'admin' | 'asistente';
};

export function createUser(body: CreateUserInput) {
  return apiFetch<{ id: string; setupUrl?: string }>('/api/admin/users', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export type SetupLink = { url: string; expiresAt: string };

export function generateSetupLink(userId: string): Promise<SetupLink> {
  return apiFetch<SetupLink>(`/api/admin/users/${userId}/reset-link`, { method: 'POST' });
}

export function validateSetupToken(token: string): Promise<{ valid: boolean; expiresAt?: string }> {
  return apiFetch(`/api/password-setup/validate?token=${encodeURIComponent(token)}`);
}

export function redeemSetupToken(token: string, password: string): Promise<{ ok: true }> {
  return apiFetch('/api/password-setup/redeem', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

export type Department = { id: string; name: string; countryCode: string };
export type Municipality = { id: string; name: string; departmentId: string };

export function listDepartments(country = 'GT'): Promise<Department[]> {
  return apiFetch<Department[]>(`/api/geo/departments?country=${encodeURIComponent(country)}`);
}

export function listMunicipalities(departmentId: string): Promise<Municipality[]> {
  return apiFetch<Municipality[]>(
    `/api/geo/municipalities?department=${encodeURIComponent(departmentId)}`,
  );
}

export type ProjectStatus = 'active' | 'archived';

export type Project = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  address: string | null;
  countryCode: string;
  countryName: string;
  departmentId: string | null;
  departmentName: string | null;
  municipalityId: string | null;
  municipalityName: string | null;
  status: ProjectStatus;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export function listProjects(status: 'active' | 'archived' | 'all' = 'active'): Promise<Project[]> {
  return apiFetch<Project[]>(`/api/projects?status=${status}`);
}

export function getProject(id: string): Promise<Project> {
  return apiFetch<Project>(`/api/projects/${id}`);
}

export type CreateProjectInput = {
  code: string;
  name: string;
  description?: string | null;
  address?: string | null;
  countryCode: string;
  departmentId?: string | null;
  municipalityId?: string | null;
};

export function createProject(body: CreateProjectInput) {
  return apiFetch<{ id: string }>('/api/projects', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export type UpdateProjectInput = {
  name?: string;
  description?: string | null;
  address?: string | null;
  countryCode?: string;
  departmentId?: string | null;
  municipalityId?: string | null;
};

export function updateProject(id: string, body: UpdateProjectInput) {
  return apiFetch<{ ok: true }>(`/api/projects/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function archiveProject(id: string) {
  return apiFetch<{ ok: true }>(`/api/projects/${id}/archive`, { method: 'POST' });
}

export function unarchiveProject(id: string) {
  return apiFetch<{ ok: true }>(`/api/projects/${id}/unarchive`, { method: 'POST' });
}

export type ClientStatus = 'active' | 'archived';
export type DocumentType = 'dpi' | 'passport' | 'nit' | 'other';

export type ClientListRow = {
  id: string;
  fullName: string;
  documentType: DocumentType;
  documentNumber: string;
  email: string | null;
  phone: string | null;
  status: ClientStatus;
  code: string;
  membershipId: string;
  assignedAt: string;
  createdAt: string;
  contractCount: number;
};

export type ClientMembership = {
  id: string;
  clientId: string;
  projectId: string;
  projectCode: string;
  projectName: string;
  code: string;
  sequence: number;
  assignedAt: string;
};

export type ClientLotSummary = {
  contractId: string;
  contractNumber: string;
  projectId: string;
  number: string;
  manzana: string;
  status: 'available' | 'reserved' | 'sold';
};

export type ClientDetail = {
  id: string;
  fullName: string;
  documentType: DocumentType;
  documentNumber: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  notes: string | null;
  status: ClientStatus;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  memberships: ClientMembership[];
  lots: ClientLotSummary[];
  accountOwnerSellerId: string | null;
  accountOwnerSellerName: string | null;
};

export type ClientContractSummary = {
  id: string;
  contractNumber: string;
  status: ContractStatus;
  financedAmount: string;
  termMonths: number;
  interestRateAnnual: string;
  balancePending: string;
  nextDueDate: string | null;
  nextDueAmount: string | null;
};

export type CreateClientInput = {
  projectId: string;
  fullName: string;
  documentType: DocumentType;
  documentNumber: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  notes?: string | null;
  accountOwnerSellerId?: string | null;
};

export type UpdateClientInput = {
  fullName?: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  notes?: string | null;
  accountOwnerSellerId?: string | null;
};

export type ClientConflictError = {
  error: string;
  conflict: 'document' | 'email';
  existingClientId?: string;
  existingClientName?: string;
};

export function listClients(params: {
  projectId: string;
  status?: 'active' | 'archived' | 'all';
  q?: string;
}): Promise<ClientListRow[]> {
  const qs = new URLSearchParams({ projectId: params.projectId });
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  return apiFetch<ClientListRow[]>(`/api/clients?${qs.toString()}`);
}

export function getClient(id: string): Promise<ClientDetail> {
  return apiFetch<ClientDetail>(`/api/clients/${id}`);
}

export function listClientContracts(
  clientId: string,
  projectId: string,
): Promise<ClientContractSummary[]> {
  const qs = new URLSearchParams({ projectId });
  return apiFetch<ClientContractSummary[]>(`/api/clients/${clientId}/contracts?${qs.toString()}`);
}

export async function createClient(body: CreateClientInput): Promise<
  { id: string; code: string } | { conflict: ClientConflictError }
> {
  const res = await fetch('/api/clients', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 409) {
    return { conflict: (await res.json()) as ClientConflictError };
  }
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? `request failed (${res.status})`);
  }
  return (await res.json()) as { id: string; code: string };
}

export function updateClient(id: string, body: UpdateClientInput) {
  return apiFetch<{ ok: true }>(`/api/clients/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function linkClientToProject(
  clientId: string,
  projectId: string,
): Promise<{ id: string; code: string } | { conflict: { code?: string; error: string } }> {
  const res = await fetch(`/api/clients/${clientId}/projects`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ projectId }),
  });
  if (res.status === 409) {
    return { conflict: (await res.json()) as { code?: string; error: string } };
  }
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? `request failed (${res.status})`);
  }
  return (await res.json()) as { id: string; code: string };
}

export function archiveClient(id: string) {
  return apiFetch<{ ok: true }>(`/api/clients/${id}/archive`, { method: 'POST' });
}

export function unarchiveClient(id: string) {
  return apiFetch<{ ok: true }>(`/api/clients/${id}/unarchive`, { method: 'POST' });
}

export type AmortizationMethod = 'french' | 'german';
export type DayCount = '30/360' | 'actual/actual';
export type ContractStatus = 'draft' | 'active' | 'completed' | 'cancelled';
export type InstallmentStatus = 'pending' | 'partial' | 'paid' | 'overdue';

export type SaleType = 'financed' | 'cash';

export type TitlingStage = 'desmembrado' | 'escriturado' | 'finalizado';

export type ContractListRow = {
  id: string;
  contractNumber: string;
  buyerId: string;
  buyerName: string;
  buyerCode: string | null;
  financedAmount: string;
  termMonths: number;
  interestRateAnnual: string;
  method: AmortizationMethod;
  saleType: SaleType;
  dayCount: DayCount;
  status: ContractStatus;
  startDate: string;
  createdAt: string;
  sellerName: string | null;
  titlingStage: TitlingStage | null;
};

export type ContractSchedule = {
  id: string;
  contractId: string;
  method: AmortizationMethod;
  dayCount: DayCount;
  roundingMode: string;
  totalPrincipal: string;
  totalInterest: string;
  totalPaid: string;
  version: number;
  isCurrent: boolean;
  generatedAt: string;
};

export type ContractInstallment = {
  id: string;
  scheduleId: string;
  installmentNumber: number;
  dueDate: string;
  openingBalance: string;
  principalAmount: string;
  interestAmount: string;
  totalAmount: string;
  closingBalance: string;
  paidPrincipal: string;
  paidInterest: string;
  adminFeeAmount: string;
  paidAdminFee: string;
  paidMora: string;
  moraAccrued?: string;
  moraOutstanding?: string;
  adminFeeOutstanding?: string;
  status: InstallmentStatus;
  displayStatus?: InstallmentStatus;
  paidAt: string | null;
};

export type ContractBalance = {
  totalDue: string;
  totalPaid: string;
  outstanding: string;
  overdueCount: number;
  nextDueDate: string | null;
  adminFeeDue?: string;
  adminFeePaid?: string;
  adminFeeOutstanding?: string;
  moraOutstanding?: string;
  totalOutstanding?: string;
};

export type ContractLotSummary = {
  id: string;
  number: string;
  manzana: string;
  areaM2: string | null;
  listPrice: string | null;
  status: 'available' | 'reserved' | 'sold';
  address: string | null;
};

export type ContractDetail = {
  id: string;
  contractNumber: string;
  projectId: string;
  buyerId: string;
  buyerName: string;
  buyerDocumentType: string;
  buyerDocumentNumber: string;
  buyerCode: string | null;
  financedAmount: string;
  downPayment: string;
  interestRateAnnual: string;
  moraMonthlyRate: string;
  moraGraceDays: number;
  termMonths: number;
  method: AmortizationMethod;
  saleType: SaleType;
  dayCount: DayCount;
  status: ContractStatus;
  titlingStage: TitlingStage | null;
  startDate: string;
  signedAt: string | null;
  createdAt: string;
  schedule: ContractSchedule | null;
  installments: ContractInstallment[];
  balance?: ContractBalance;
  waterRight?: {
    amount: string;
    paidAt: string | null;
    status: 'paid' | 'due' | 'pending' | 'na';
  };
  lot: ContractLotSummary | null;
  sellerId: string | null;
  sellerName: string | null;
};

export type CreateContractFinanced = {
  saleType?: 'financed';
  projectId: string;
  buyerId: string;
  financedAmount: string;
  downPayment: string;
  interestRateAnnual: string;
  termMonths: number;
  method: AmortizationMethod;
  dayCount: DayCount;
  startDate: string;
  adminFee?: string;
  moraMonthlyRate?: string;
  moraGraceDays?: number;
  interestStartMonth?: number;
  lot: { number: string; manzana: string; areaM2?: string; listPrice?: string; address?: string };
  sellerId?: string | null;
};

export type CreateContractCash = {
  saleType: 'cash';
  projectId: string;
  buyerId: string;
  financedAmount: string;
  startDate: string;
  lot: { number: string; manzana: string; areaM2?: string; listPrice?: string; address?: string };
  payment: { method: PaymentMethod; paymentDate: string; reference?: string; notes?: string };
  sellerId?: string | null;
};

export type CreateContractInput = CreateContractFinanced | CreateContractCash;

export function listContracts(params: {
  projectId: string;
  status?: 'active' | 'completed' | 'cancelled' | 'all';
  q?: string;
  buyerId?: string;
  hasOverdue?: boolean;
}): Promise<ContractListRow[]> {
  const qs = new URLSearchParams({ projectId: params.projectId });
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  if (params.buyerId) qs.set('buyerId', params.buyerId);
  if (params.hasOverdue) qs.set('hasOverdue', '1');
  return apiFetch<ContractListRow[]>(`/api/contracts?${qs.toString()}`);
}

export function getContract(id: string): Promise<ContractDetail> {
  return apiFetch<ContractDetail>(`/api/contracts/${id}`);
}

export function createContract(
  body: CreateContractInput,
): Promise<{ id: string; contractNumber: string; paymentId?: string }> {
  return apiFetch<{ id: string; contractNumber: string; paymentId?: string }>('/api/contracts', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function cancelContract(id: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/contracts/${id}/cancel`, { method: 'POST' });
}

export function voidPayment(paymentId: string, reason?: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/payments/${paymentId}/void`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export function payWaterRight(id: string): Promise<{ id: string; waterRightPaidAt: string }> {
  return apiFetch<{ id: string; waterRightPaidAt: string }>(`/api/contracts/${id}/derecho-agua/pagar`, { method: 'POST' });
}

export function revertWaterRight(id: string): Promise<{ id: string; waterRightPaidAt: null }> {
  return apiFetch<{ id: string; waterRightPaidAt: null }>(`/api/contracts/${id}/derecho-agua/revertir`, { method: 'POST' });
}

export function setTitlingStage(
  id: string,
  titlingStage: TitlingStage | null,
): Promise<{ id: string; titlingStage: TitlingStage | null }> {
  return apiFetch<{ id: string; titlingStage: TitlingStage | null }>(`/api/contracts/${id}/etapa`, {
    method: 'POST',
    body: JSON.stringify({ titlingStage }),
  });
}

export type PaymentMethod = 'cash' | 'transfer' | 'deposit' | 'check';
export type ImputationOrder = 'interest_first' | 'principal_first';

export type ImputationLine = {
  installmentId: string;
  appliedToMora: string;
  appliedToAdminFee: string;
  appliedToInterest: string;
  appliedToPrincipal: string;
};
export type ImputationSnapshot = {
  order: ImputationOrder;
  unapplied: string;
  lines: ImputationLine[];
};

export type PaymentStatus = 'posted' | 'voided';

export type PaymentRow = {
  id: string;
  paymentDate: string;
  amount: string;
  method: PaymentMethod;
  reference: string | null;
  notes: string | null;
  imputationApplied: ImputationSnapshot;
  createdAt: string;
  hasReceipt: boolean;
  paymentType: 'regular' | 'abono_capital';
  principalPaydown: string;
  payerName: string | null;
  concept: string | null;
  receiptMeta: {
    saldoAnterior: string;
    saldoNuevo: string;
    cuotaNumber: number;
    branding: { brandColor: string | null; footer: string | null };
  } | null;
  receiptNumber: string | null;
  status: PaymentStatus;
  voidedAt: string | null;
  voidReason: string | null;
  receiptReplacedAt: string | null;
};

export type PaydownMode = 'reduce_cuota' | 'reduce_plazo';
export type RegisterPaymentInput = {
  contractId: string;
  paymentDate: string;
  amount: string;
  method: PaymentMethod;
  reference?: string;
  notes?: string;
  paymentType?: 'regular' | 'abono_capital';
  paydownMode?: PaydownMode;
  payerName?: string;
  concept?: string;
};

export function registerPayment(
  body: RegisterPaymentInput,
): Promise<{
  id: string;
  appliedTotal: string;
  unapplied: string;
  principalPaydown?: string;
  newVersion?: number;
  receiptNumber: string;
}> {
  return apiFetch('/api/payments', { method: 'POST', body: JSON.stringify(body) });
}

export function listContractPayments(contractId: string): Promise<PaymentRow[]> {
  return apiFetch<PaymentRow[]>(`/api/contracts/${contractId}/payments`);
}

export async function uploadReceipt(paymentId: string, file: File): Promise<{ id: string }> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`/api/payments/${paymentId}/receipt`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? `upload failed (${res.status})`);
  }
  return (await res.json()) as { id: string };
}

export function receiptUrl(paymentId: string): string {
  return `/api/payments/${paymentId}/receipt`;
}

export type DocumentKind = 'dpi' | 'contrato' | 'recibo' | 'voucher' | 'otro';

export type DocumentRow = {
  id: string;
  clientId: string | null;
  contractId: string | null;
  kind: DocumentKind;
  label: string | null;
  originalFilename: string;
  contentType: string;
  fileSize: number;
  uploadedBy: string;
  uploadedByName: string | null;
  uploadedAt: string;
};

export function listClientDocuments(clientId: string): Promise<DocumentRow[]> {
  return apiFetch<DocumentRow[]>(`/api/clients/${clientId}/documents`);
}

export function listContractDocuments(contractId: string): Promise<DocumentRow[]> {
  return apiFetch<DocumentRow[]>(`/api/contracts/${contractId}/documents`);
}

async function uploadDocument(
  path: string,
  file: File,
  kind: DocumentKind,
  label?: string,
): Promise<{ id: string }> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('kind', kind);
  if (label && label.trim()) fd.append('label', label.trim());
  const res = await fetch(path, { method: 'POST', credentials: 'include', body: fd });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? `upload failed (${res.status})`);
  }
  return (await res.json()) as { id: string };
}

export function uploadClientDocument(clientId: string, file: File, kind: DocumentKind, label?: string): Promise<{ id: string }> {
  return uploadDocument(`/api/clients/${clientId}/documents`, file, kind, label);
}

export function uploadContractDocument(contractId: string, file: File, kind: DocumentKind, label?: string): Promise<{ id: string }> {
  return uploadDocument(`/api/contracts/${contractId}/documents`, file, kind, label);
}

export async function deleteDocument(docId: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`/api/documents/${docId}`, { method: 'DELETE' });
}

export function documentContentUrl(docId: string): string {
  return `/api/documents/${docId}/content`;
}

export type ProjectSettings = {
  projectId: string;
  imputationOrder: ImputationOrder;
  roundingMode: string;
  currencyCode: string;
  defaultDayCount: DayCount;
  defaultAdminFee: string;
  defaultMoraMonthlyRate: string;
  defaultMoraGraceDays: number;
  defaultWaterRight: string;
  updatedAt: string;
  brandColor: string | null;
  receiptFooter: string | null;
  hasLogo: boolean;
};

export function getProjectSettings(projectId: string): Promise<ProjectSettings> {
  return apiFetch<ProjectSettings>(`/api/projects/${projectId}/settings`);
}

export type ProjectSettingsPatch = Partial<{
  imputationOrder: ImputationOrder;
  defaultAdminFee: string;
  defaultMoraMonthlyRate: string;
  defaultMoraGraceDays: number;
  defaultWaterRight: string;
  brandColor: string | null;
  receiptFooter: string | null;
}>;

export function updateProjectSettings(projectId: string, patch: ProjectSettingsPatch): Promise<{ ok: true }> {
  return apiFetch(`/api/projects/${projectId}/settings`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function uploadProjectLogo(projectId: string, file: File): Promise<{ ok: true }> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`/api/projects/${projectId}/logo`, { method: 'POST', credentials: 'include', body: fd });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? `upload failed (${res.status})`);
  }
  return { ok: true };
}
export function deleteProjectLogo(projectId: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/projects/${projectId}/logo`, { method: 'DELETE' });
}
export function projectLogoUrl(projectId: string): string {
  return `/api/projects/${projectId}/logo`;
}

export type DashboardResponse = {
  projectId: string;
  range: { from: string; to: string };
  comercial: {
    activeContractsCount: number;
    newContractsCount: number;
    contadoSalesCount: number;
    totalFinanced: string;
    averageTicket: string;
  };
  financiero: {
    outstandingPortfolio: string;
    periodCollections: string;
    overdueCount: number;
    currentCount: number;
    upcoming30dCount: number;
    upcoming30dAmount: string;
    moraOutstanding: string;
    aging: { d1_30: string; d31_60: string; d61_90: string; d90plus: string };
  };
  previous: {
    periodCollections: string;
    newContractsCount: number;
    contadoSalesCount: number;
  };
  trends: {
    monthlyCollections: Array<{ month: string; amount: string }>;
  };
};

export function getDashboard(params: {
  projectId: string;
  from: string;
  to: string;
}): Promise<DashboardResponse> {
  const qs = new URLSearchParams(params).toString();
  return apiFetch<DashboardResponse>(`/api/dashboard?${qs}`);
}

export type AuditEvent = {
  id: string;
  actorUserId: string;
  actorName: string | null;
  actorEmail: string | null;
  projectId: string | null;
  entityType: string;
  entityId: string;
  action: string;
  before: unknown;
  after: unknown;
  requestId: string | null;
  createdAt: string;
};

export type AuditListResponse = { rows: AuditEvent[]; total: number };

export function listAuditEvents(params: {
  entityType?: string;
  action?: string;
  actorUserId?: string;
  projectId?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}): Promise<AuditListResponse> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, String(v));
  }
  return apiFetch<AuditListResponse>(`/api/audit?${qs.toString()}`);
}

// ---- Sellers (vendedores) ----
export type SellerStatus = 'active' | 'archived';

export type SellerListRow = {
  id: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  userId: string | null;
  status: SellerStatus;
  createdAt: string;
  dealCount: number;
};

export type SellerDetail = {
  id: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  userId: string | null;
  notes: string | null;
  status: SellerStatus;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

/** Compact row used to populate seller <select> pickers. */
export type SellerOption = Pick<SellerListRow, 'id' | 'fullName'>;

export type SellerContractSummary = {
  id: string;
  contractNumber: string;
  status: ContractStatus;
  financedAmount: string;
  termMonths: number;
  interestRateAnnual: string;
  balancePending: string;
  nextDueDate: string | null;
  nextDueAmount: string | null;
};

export type CreateSellerInput = {
  fullName: string;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
};

export type UpdateSellerInput = {
  fullName?: string;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
};

export type SellerConflictError = { error: string; conflict: 'email' };

export function listSellers(params?: {
  status?: 'active' | 'archived' | 'all';
  q?: string;
}): Promise<SellerListRow[]> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.q) qs.set('q', params.q);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<SellerListRow[]>(`/api/sellers${suffix}`);
}

export function getSeller(id: string): Promise<SellerDetail> {
  return apiFetch<SellerDetail>(`/api/sellers/${id}`);
}

export function listSellerContracts(
  id: string,
  projectId?: string,
): Promise<SellerContractSummary[]> {
  const qs = new URLSearchParams();
  if (projectId) qs.set('projectId', projectId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<SellerContractSummary[]>(`/api/sellers/${id}/contracts${suffix}`);
}

export async function createSeller(
  body: CreateSellerInput,
): Promise<{ id: string } | { conflict: SellerConflictError }> {
  const res = await fetch('/api/sellers', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 409) return { conflict: (await res.json()) as SellerConflictError };
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? `request failed (${res.status})`);
  }
  return (await res.json()) as { id: string };
}

export function updateSeller(id: string, body: UpdateSellerInput) {
  return apiFetch<{ ok: true }>(`/api/sellers/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function archiveSeller(id: string) {
  return apiFetch<{ ok: true }>(`/api/sellers/${id}/archive`, { method: 'POST' });
}

export function unarchiveSeller(id: string) {
  return apiFetch<{ ok: true }>(`/api/sellers/${id}/unarchive`, { method: 'POST' });
}

// ---- Reports (reportería) ----
export type ReportStatus = 'active' | 'completed' | 'cancelled' | 'all';

export type CarteraContractRow = {
  contractId: string;
  contractNumber: string;
  buyerName: string;
  buyerCode: string | null;
  sellerName: string | null;
  manzana: string | null;
  lotNumber: string | null;
  saleType: SaleType;
  status: ContractStatus;
  financedAmount: string;
  termMonths: number;
  interestRateAnnual: string;
  method: AmortizationMethod;
  startDate: string;
  outstanding: string;
  overdueCount: number;
  moraOutstanding: string;
  overdueBalance: string;
  adminFeeOutstanding: string;
  totalOutstanding: string;
  nextDueDate: string | null;
  waterRightStatus: 'paid' | 'due' | 'pending' | 'na';
};

export type CarteraClientRow = {
  code: string | null;
  fullName: string;
  documentType: string;
  documentNumber: string;
  email: string | null;
  phone: string | null;
  address: string | null;
};

export type CarteraResponse = { contracts: CarteraContractRow[]; clients: CarteraClientRow[] };

export type StatementWithPayments = ContractDetail & { payments: PaymentRow[] };

export function getCartera(projectId: string, status: ReportStatus = 'active'): Promise<CarteraResponse> {
  return apiFetch<CarteraResponse>(`/api/reports/cartera?projectId=${projectId}&status=${status}`);
}

export function getEstadosCuenta(
  projectId: string,
  status: ReportStatus = 'active',
): Promise<StatementWithPayments[]> {
  return apiFetch<StatementWithPayments[]>(`/api/reports/estados-cuenta?projectId=${projectId}&status=${status}`);
}
